import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Space, Steps, Typography } from 'antd';
import { _T } from '../../base-react/src/Localization';
import { useCompetition } from '../../base-react/hooks/Competition';
import { useUserSettings } from '../../base-react/hooks/Storage';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
const { Text, Title } = Typography;
const GUIDE_LAST_STEP = 6;
const GUIDE_DISABLED_STEP = -1;
const STEP_TITLE_KEYS = [
    'onboarding.drawer.steps.1',
    'onboarding.drawer.steps.2',
    'onboarding.drawer.steps.3',
    'onboarding.drawer.steps.4',
    'onboarding.drawer.steps.5',
    'onboarding.drawer.steps.6',
    'onboarding.drawer.steps.7',
];
const parseStoredGuideStep = (value, eventId) => {
    if (!value) {
        return undefined;
    }
    const [storedEventId, storedStepRaw] = value.split('/');
    if (storedEventId !== String(eventId)) {
        return undefined;
    }
    const storedStep = Number(storedStepRaw);
    if (storedStep === GUIDE_DISABLED_STEP) {
        return GUIDE_DISABLED_STEP;
    }
    if (Number.isFinite(storedStep) && storedStep >= 0 && storedStep <= GUIDE_LAST_STEP) {
        return storedStep;
    }
    return undefined;
};
export const OnboardingDrawer = ({ event_id }) => {
    var _a;
    const location = useLocation();
    const navigate = useNavigate();
    const { settings, updateSettings } = useUserSettings();
    const { competition_id } = useParams();
    const [inputCompetition, , competitions] = useCompetition(event_id);
    const activeCompetition = useMemo(() => {
        if (!competitions || competitions.length === 0) {
            return inputCompetition;
        }
        if (competition_id) {
            return competitions.find((item) => item.competition_id === competition_id) || inputCompetition;
        }
        return competitions[0];
    }, [competition_id, competitions, inputCompetition]);
    const activeCompetitionId = (activeCompetition === null || activeCompetition === void 0 ? void 0 : activeCompetition.competition_id) || competition_id || event_id;
    const isPracticeCompetition = Number(activeCompetition === null || activeCompetition === void 0 ? void 0 : activeCompetition.competition_level) === 0;
    const savedGuideStep = parseStoredGuideStep(settings.managerOnboardingGuideStepInfo, event_id);
    const hasGuideState = savedGuideStep != null;
    const isGuideDisabled = savedGuideStep === GUIDE_DISABLED_STEP;
    const currentStep = savedGuideStep != null && savedGuideStep !== GUIDE_DISABLED_STEP ? savedGuideStep : 0;
    const [isOpen, setIsOpen] = useState(false);
    const shouldOpenGuide = ((_a = location.state) === null || _a === void 0 ? void 0 : _a.openGuide) === true;
    const isGuideActive = !isGuideDisabled && (hasGuideState || shouldOpenGuide);
    const paths = {
        competitions: `/event/${event_id}/competitions`,
        classes: `/event/${event_id}/comp/${activeCompetitionId}/classes`,
        teams: `/event/${event_id}/comp/${activeCompetitionId}/teams`,
        judges: `/event/${event_id}/comp/${activeCompetitionId}/judges`,
        schedule: `/event/${event_id}/schedule`,
    };
    const stepPathMap = {
        0: paths.competitions,
        1: paths.schedule,
        2: paths.classes,
        3: paths.teams,
        4: paths.classes,
        5: paths.judges,
        6: paths.schedule,
    };
    const getPathForStep = (step) => stepPathMap[step];
    const persistGuideStep = (step) => {
        const nextValue = `${event_id}/${step}`;
        if (settings.managerOnboardingGuideStepInfo !== nextValue) {
            updateSettings({ managerOnboardingGuideStepInfo: nextValue });
        }
    };
    useEffect(() => {
        setIsOpen(isGuideActive);
    }, [event_id, isGuideActive]);
    useEffect(() => {
        if (shouldOpenGuide && !isGuideDisabled) {
            if (!hasGuideState) {
                persistGuideStep(0);
            }
            setIsOpen(true);
            navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
        }
    }, [hasGuideState, isGuideDisabled, location.pathname, location.search, navigate, shouldOpenGuide]);
    const goToGuideStep = (step, path) => {
        const targetPath = path || location.pathname;
        persistGuideStep(step);
        setIsOpen(true);
        if (targetPath !== location.pathname) {
            navigate(targetPath);
        }
    };
    const onOpenGuide = () => goToGuideStep(currentStep, getPathForStep(currentStep));
    const onShowGuideStep = () => {
        persistGuideStep(currentStep);
        setIsOpen(false);
    };
    const onDisableGuide = () => {
        persistGuideStep(GUIDE_DISABLED_STEP);
        setIsOpen(false);
    };
    const stepItems = STEP_TITLE_KEYS.map((titleKey) => ({ title: _T(titleKey) }));
    const renderStep = (titleKey, description, continueTo, extra) => (React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
        React.createElement(Title, { level: 5 }, _T(titleKey)),
        React.createElement(Text, { type: "secondary" }, description),
        extra,
        continueTo != null && (React.createElement(Space, { wrap: true },
            React.createElement(Button, { onClick: onShowGuideStep }, _T('onboarding.drawer.action.show')),
            React.createElement(Button, { type: "primary", onClick: () => goToGuideStep(continueTo, getPathForStep(continueTo)) }, _T('onboarding.drawer.action.continue'))))));
    const stepContent = (() => {
        switch (currentStep) {
            case 0:
                return renderStep('onboarding.drawer.structure.title', _T('onboarding.drawer.structure.description'), 1);
            case 1:
                return renderStep('onboarding.drawer.schedule.title', _T('onboarding.drawer.schedule.description'), 2);
            case 2:
                return renderStep('onboarding.drawer.classes.title', _T('onboarding.drawer.classes.description'), 3);
            case 3:
                return renderStep('onboarding.drawer.participants.title', _T('onboarding.drawer.participants.description'), 4, (activeCompetition === null || activeCompetition === void 0 ? void 0 : activeCompetition.competition_level) != null ? (React.createElement(Text, { type: isPracticeCompetition ? 'secondary' : 'warning' }, _T(isPracticeCompetition
                    ? 'onboarding.drawer.participants.testing-practice'
                    : 'onboarding.drawer.participants.testing-disabled', { action: _T('team.generate') }))) : undefined);
            case 4:
                return renderStep('onboarding.drawer.rounds.title', _T('onboarding.drawer.rounds.description', { action: _T('guide.title') }), 5);
            case 5:
                return renderStep('onboarding.drawer.judges.title', _T('onboarding.drawer.judges.description'), 6);
            default:
                return renderStep('onboarding.drawer.finish.title', _T('onboarding.drawer.finish.description'));
        }
    })();
    const footerAction = (React.createElement("div", { style: { display: 'flex', justifyContent: 'center', width: '100%' } },
        React.createElement(Space, { wrap: true },
            React.createElement(Button, { onClick: onDisableGuide }, _T('onboarding.drawer.action.disable')),
            currentStep === GUIDE_LAST_STEP && (React.createElement(Button, { onClick: onDisableGuide }, _T('onboarding.drawer.finish.close'))))));
    if (!isGuideActive) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        !isOpen && (React.createElement(Button, { type: "primary", size: "large", style: {
                position: 'fixed',
                left: '50%',
                bottom: 24,
                transform: 'translateX(-50%)',
                zIndex: 1100,
                minWidth: 260,
                minHeight: 52
            }, onClick: onOpenGuide }, _T('onboarding.drawer.open'))),
        isOpen && (React.createElement(Drawer, { title: _T('onboarding.drawer.title'), open: isOpen, onClose: onShowGuideStep, size: "default", mask: true, closable: true, destroyOnHidden: true, extra: React.createElement(Button, { type: "text", onClick: onShowGuideStep }, _T('common.close')), footer: footerAction, styles: {
                body: {
                    overflowY: 'auto'
                }
            } },
            React.createElement("div", { style: { width: '100%', display: 'flex', flexDirection: 'column', gap: 24 } },
                React.createElement(Steps, { current: currentStep, items: stepItems, orientation: "vertical", onChange: (nextStep) => {
                        const step = nextStep;
                        goToGuideStep(step, getPathForStep(step));
                    } }),
                stepContent)))));
};
